/**
 * KK-523 / KK-504 · Okul geneli stok görünümü.
 *
 * EKRANIN VAR OLMA SEBEBİ:
 *   "Toplam 40 var ama 38'i depoda" durumunun GÖRÜNÜR olması. Tek bir toplam
 *   sayı, rafta mal yokken "stok var" hissi üretir ve sipariş geç verilir.
 *   Bu yüzden her ürün satırı hem KONDİSYON kırılımını (sağlam / karantina /
 *   imha bekliyor / tedarikçi iade) hem de NOKTA dağılımını taşır.
 *
 * KK-504 düşük stok yalnızca SAĞLAM stoğu sayar: karantinadaki mal satışa
 * hazır değildir, onu "var" saymak doğrudan raf boşluğu demektir.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Buton,
  Kart,
  Rozet,
  Secim,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Uyari,
} from "@kantin/ui";
import { STOK_DURUM_ADI, StokDurumu, miktarTopla } from "@kantin/shared";
import { apiOku } from "./api.js";
import { noktaSecenekleri, useNoktalar } from "./kancalar.js";
import {
  AramaKutusu,
  Sayfa,
  SayfaBasligi,
  Sekmeler,
  SorguDurumu,
  miktarGoster,
} from "./ortak.js";

interface StokDagilimi {
  nokta_id: string;
  nokta_ad: string;
  nokta_tur: number;
  depo_mu: boolean;
  durum: number;
  durum_adi: string;
  miktar: string;
  kritik_seviye: string | null;
}

interface StokUrunu {
  urun_id: string;
  urun_ad: string;
  toplam: string;
  /** Anahtar: StokDurumu kodu ("1".."4"), değer: miktar string */
  kondisyon: Record<string, string>;
  dagilim: StokDagilimi[];
}

interface DusukStokSatiri {
  nokta_id: string;
  nokta_ad: string;
  urun_id: string;
  urun_ad: string;
  miktar: string;
  kritik_seviye: string;
}

type Sekme = "genel" | "dusuk";

const KONDISYON_SIRASI = [
  StokDurumu.SAGLAM,
  StokDurumu.KARANTINA,
  StokDurumu.IMHA_BEKLIYOR,
  StokDurumu.TEDARIKCI_IADE,
];

const KONDISYON_ROZETI: Record<number, "onay" | "uyari" | "hata" | "notr"> = {
  [StokDurumu.SAGLAM]: "onay",
  [StokDurumu.KARANTINA]: "uyari",
  [StokDurumu.IMHA_BEKLIYOR]: "hata",
  [StokDurumu.TEDARIKCI_IADE]: "notr",
};

export function Stok() {
  const [sekme, setSekme] = useState<Sekme>("genel");

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Stok"
        aciklama="Okul geneli stok görünümü. Bir ürünün deposu ve noktaları tek satırda, kondisyon kırılımı ile birlikte."
      />
      <Sekmeler<Sekme>
        etiket="Stok görünümleri"
        secili={sekme}
        sec={setSekme}
        sekmeler={[
          { deger: "genel", metin: "Okul geneli" },
          { deger: "dusuk", metin: "Düşük stok" },
        ]}
      />
      {sekme === "genel" ? <GenelStok /> : <DusukStok />}
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

function GenelStok() {
  const [arama, setArama] = useState("");
  const [noktaId, setNoktaId] = useState("");
  const [durum, setDurum] = useState("");
  const [acik, setAcik] = useState<string | null>(null);
  const noktalar = useNoktalar({ aktif: true });

  const sorgu = useQuery({
    queryKey: ["stok", arama, noktaId, durum],
    queryFn: () =>
      apiOku<{ urunler: StokUrunu[] }>("/stok", {
        arama: arama.trim() || undefined,
        nokta_id: noktaId || undefined,
        durum: durum || undefined,
      }),
  });

  const urunler = sorgu.data?.urunler ?? [];
  // Nokta görevlisinin kapsamında depo yoktur: yanıta depo satırı hiç gelmez.
  // O durumda "Depoda 0" yazmak deponun boş olduğu izlenimi verir; depo
  // yalnızca kapsam dışıdır. Depo satırı yoksa yalnız nokta miktarı yazılır.
  const depoGorunur = urunler.some((u) => u.dagilim.some((d) => d.depo_mu));
  const noktaSayisi = new Set(
    urunler.flatMap((u) => u.dagilim.map((d) => d.nokta_id)),
  ).size;

  return (
    <>
      <Kart baslik="Süzgeçler">
        <div className="grid gap-3 md:grid-cols-3">
          {/* Okuyucu klavye gibi yazıp Enter basar; kutu form dışında olduğu
              için Enter zararsızdır, 300 ms duraklama sorguyu tetikler. */}
          <AramaKutusu
            etiket="Ara"
            ipucu="Ürün adı veya barkod okutun"
            deger={arama}
            degistir={setArama}
          />
          <Secim
            etiket="Nokta"
            value={noktaId}
            onChange={(e) => setNoktaId(e.target.value)}
            secenekler={noktaSecenekleri(noktalar.data?.noktalar)}
          />
          <Secim
            etiket="Kondisyon"
            value={durum}
            onChange={(e) => setDurum(e.target.value)}
            secenekler={[
              { deger: "", metin: "Tüm kondisyonlar" },
              ...KONDISYON_SIRASI.map((d) => ({
                deger: String(d),
                metin: STOK_DURUM_ADI[d] ?? "Bilinmiyor",
              })),
            ]}
          />
        </div>
      </Kart>

      <Kart
        baslik="Ürün bazında stok"
        ustBilgi={
          <span className="text-sm text-murekkep-3">
            <span className="rakam font-mono">{urunler.length}</span> ürün
          </span>
        }
      >
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={urunler.length === 0}
          bosBaslik={arama.trim() ? "Eşleşen ürün yok" : "Stok kaydı yok"}
          bosAciklama={
            arama.trim()
              ? "Bu ad ya da barkodla stok kaydı olan ürün bulunamadı. Barkod tam eşleşir; adın bir parçası yeterlidir."
              : "Bu süzgeçte hiçbir ürünün stok hareketi bulunmuyor. Mal girişi Cari ekranındaki alım faturası ile yapılır."
          }
          yenile={() => void sorgu.refetch()}
        >
          <Tablo>
            <TabloBaslik>
              <th scope="col">Ürün</th>
              <th scope="col">Toplam</th>
              <th scope="col">Nerede</th>
              <th scope="col">Kondisyon kırılımı</th>
              <th scope="col">
                <span className="gizli-erisilebilir">Dağılım</span>
              </th>
            </TabloBaslik>
            <TabloGovde>
              {urunler.map((u) => {
                const depoda = miktarTopla(
                  ...u.dagilim.filter((d) => d.depo_mu).map((d) => d.miktar),
                );
                const noktalarda = miktarTopla(
                  ...u.dagilim.filter((d) => !d.depo_mu).map((d) => d.miktar),
                );
                const acikMi = acik === u.urun_id;
                return (
                  <tr key={u.urun_id}>
                    <td>
                      <p className="m-0 font-semibold text-murekkep">
                        {u.urun_ad}
                      </p>
                    </td>
                    <td className="rakam text-right font-mono">
                      {miktarGoster(u.toplam)}
                    </td>
                    <td className="text-sm text-murekkep-2">
                      {/* KK-523'ün cümlesi: "toplam 40 var ama 38'i depoda". */}
                      {depoGorunur ? (
                        <>
                          Depoda{" "}
                          <strong className="rakam font-mono">
                            {miktarGoster(depoda.toFixed(3))}
                          </strong>
                          , noktalarda{" "}
                        </>
                      ) : noktaSayisi > 1 ? (
                        <>Noktalarda </>
                      ) : (
                        <>Noktada </>
                      )}
                      <strong className="rakam font-mono">
                        {miktarGoster(noktalarda.toFixed(3))}
                      </strong>
                    </td>
                    <td>
                      <span className="flex flex-wrap gap-1">
                        {KONDISYON_SIRASI.map((d) => {
                          const deger = u.kondisyon[String(d)];
                          if (!deger || deger === "0.000") return null;
                          return (
                            <Rozet key={d} tur={KONDISYON_ROZETI[d] ?? "notr"}>
                              {STOK_DURUM_ADI[d] ?? "?"}{" "}
                              <span className="rakam font-mono">
                                {miktarGoster(deger)}
                              </span>
                            </Rozet>
                          );
                        })}
                      </span>
                    </td>
                    <td className="text-right">
                      <Buton
                        tur="sessiz"
                        aria-expanded={acikMi}
                        onClick={() => setAcik(acikMi ? null : u.urun_id)}
                      >
                        {acikMi ? "Gizle" : "Dağılım"}
                      </Buton>
                      {acikMi && (
                        <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0 text-left text-sm">
                          {u.dagilim.map((d, i) => (
                            <li
                              key={`${d.nokta_id}-${d.durum}-${i}`}
                              className="flex items-center justify-between gap-3 border-b border-cetvel py-1 last:border-0"
                            >
                              <span className="text-murekkep-2">
                                {d.nokta_ad}
                                {d.depo_mu && (
                                  <span className="ml-1 text-xs text-murekkep-3">
                                    (depo)
                                  </span>
                                )}
                              </span>
                              <span className="flex items-center gap-2">
                                <Rozet
                                  tur={KONDISYON_ROZETI[d.durum] ?? "notr"}
                                >
                                  {d.durum_adi}
                                </Rozet>
                                <span className="rakam font-mono">
                                  {miktarGoster(d.miktar)}
                                </span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                );
              })}
            </TabloGovde>
          </Tablo>
        </SorguDurumu>
      </Kart>
    </>
  );
}

// ---------------------------------------------------------------------------

function DusukStok() {
  const [noktaId, setNoktaId] = useState("");
  const noktalar = useNoktalar({ aktif: true });

  const sorgu = useQuery({
    queryKey: ["stok-dusuk", noktaId],
    queryFn: () =>
      apiOku<{ satirlar: DusukStokSatiri[] }>("/stok/dusuk", {
        nokta_id: noktaId || undefined,
      }),
  });

  const satirlar = sorgu.data?.satirlar ?? [];

  return (
    <>
      <Uyari tur="bilgi" baslik="Yalnızca sağlam stok sayılır">
        Karantinadaki, imha bekleyen ve tedarikçiye iade edilecek mal bu listede
        &quot;var&quot; sayılmaz — satışa hazır değildir.
      </Uyari>

      <Kart baslik="Süzgeç">
        <div className="grid gap-3 md:grid-cols-3">
          <Secim
            etiket="Nokta"
            value={noktaId}
            onChange={(e) => setNoktaId(e.target.value)}
            secenekler={noktaSecenekleri(noktalar.data?.noktalar)}
          />
        </div>
      </Kart>

      <Kart
        baslik="Kritik seviyenin altındakiler"
        ustBilgi={
          <span className="text-sm text-murekkep-3">
            <span className="rakam font-mono">{satirlar.length}</span> satır
          </span>
        }
      >
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={satirlar.length === 0}
          bosBaslik="Düşük stok yok"
          bosAciklama="Kritik seviyesi tanımlı hiçbir ürün eşiğin altında değil."
          yenile={() => void sorgu.refetch()}
        >
          <Tablo>
            <TabloBaslik>
              <th scope="col">Nokta</th>
              <th scope="col">Ürün</th>
              <th scope="col">Sağlam stok</th>
              <th scope="col">Kritik seviye</th>
              <th scope="col">Durum</th>
            </TabloBaslik>
            <TabloGovde>
              {satirlar.map((s) => (
                <tr key={`${s.nokta_id}-${s.urun_id}`}>
                  <td className="text-sm text-murekkep-2">{s.nokta_ad}</td>
                  <td className="font-semibold text-murekkep">{s.urun_ad}</td>
                  <td className="rakam text-right font-mono">
                    {miktarGoster(s.miktar)}
                  </td>
                  <td className="rakam text-right font-mono text-murekkep-3">
                    {miktarGoster(s.kritik_seviye)}
                  </td>
                  <td>
                    <Rozet tur="uyari">ikmal gerekli</Rozet>
                  </td>
                </tr>
              ))}
            </TabloGovde>
          </Tablo>
        </SorguDurumu>
      </Kart>
    </>
  );
}
